// @ts-check

/**
 * Mapbox layer specifications and styling utilities.
 * Creates layer configurations for island points and labels.
 */

/**
 * Create a Mapbox layer spec for island point markers.
 * @param {string} sourceId - The ID of the GeoJSON source
 * @returns {import('mapbox-gl').AnyLayer} Mapbox circle layer configuration
 */
export function createIslandLayer(sourceId) {
  return {
    id: "islands-layer",
    type: "circle",
    source: sourceId,
    filter: ["!", ["get", "isAssigned"]], // Only show non-assigned islands
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 2, 4, 8, 8],
      "circle-color": "#4264fb",
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
      "circle-opacity": 0.9
    }
  };
}

/**
 * Create a Mapbox layer spec for assigned island markers with star icon.
 * @param {string} sourceId - The ID of the GeoJSON source
 * @returns {import('mapbox-gl').AnyLayer} Mapbox symbol layer configuration for assigned islands
 */
export function createAssignedIslandLayer(sourceId) {
  return {
    id: "assigned-island-layer",
    type: "symbol",
    source: sourceId,
    filter: ["get", "isAssigned"], // Only show assigned islands
    layout: {
      "icon-image": "gold-star",
      "icon-size": ["interpolate", ["linear"], ["zoom"], 2, 0.6, 8, 1.0],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true
    },
    minzoom: 2
  };
}

/**
 * Create a Mapbox symbol layer for island labels.
 * Uses Mapbox's native label rendering with zoom-based visibility and collision detection.
 *
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#symbol|Mapbox Symbol Layer Documentation}
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-property|Symbol Layout Properties}
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-property|Symbol Paint Properties}
 *
 * @param {string} sourceId - The ID of the GeoJSON source
 * @returns {import('mapbox-gl').AnyLayer} Mapbox symbol layer configuration
 */
export function createIslandLabelLayer(sourceId) {
  return {
    id: "islands-labels",
    type: "symbol",
    source: sourceId,
    layout: {
      // Text content: tries name, Name, place_name properties, falls back to empty string
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#coalesce
      "text-field": [
        "coalesce",
        ["get", "name"],
        ["get", "Name"],
        ["get", "place_name"],
        ""
      ],
      // Font stack: semibold for solid, grounded feel (distinct from italic route labels)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-font
      "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
      // Text size scales with zoom: 12px at zoom 2, 14px at zoom 4, 16px at zoom 6, 18px at zoom 10
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        2,
        12, // zoom 2: 12px (larger for visibility at default zoom ~4)
        4,
        14, // zoom 4: 14px (default zoom level)
        6,
        16, // zoom 6: 16px
        10,
        18 // zoom 10: 18px
      ],
      // Offset label 1.5 units below the point (0 horizontal, 1.5 vertical)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-offset
      "text-offset": [1.5, 1.5],
      // Anchor point at bottom or right of the text (below or to the right of the point), depending on available space
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-variable-anchor
      "text-variable-anchor": ["bottom", "right"],
      // base on the text-variable-anchor, justify the text
      "text-justify": "auto",
      // Collision detection: labels won't overlap with other labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-allow-overlap
      "text-allow-overlap": false,
      // Respect placement of other labels (participates in collision detection)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-ignore-placement
      "text-ignore-placement": false,
      // Labels are required (always shown when possible) - island labels have priority over region labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-optional
      "text-optional": false,
      // Subtle letter spacing for refined appearance
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-letter-spacing
      "text-letter-spacing": 0.02
    },
    paint: {
      // Cool off-white with subtle blue tint (distinct from warm route labels)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-color
      "text-color": "#f0f4f8",
      // Deep ocean blue-gray halo (cooler tone, complements island/water theme)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-color
      "text-halo-color": "#1a2633",
      // Refined halo width
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-width
      "text-halo-width": 1.5,
      // Crisp halo blur for clean edges
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-blur
      "text-halo-blur": 0.5
    },
    // Labels appear starting at zoom level 2 (matches map minZoom)
    // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#minzoom
    minzoom: 2
  };
}
